import React from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  HStack,
  IconButton,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  Input,
  Select,
} from '@chakra-ui/react';
import { FaEye, FaEdit, FaTrash, FaEllipsisV } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { PermissionGuard } from './PermissionGuard';
import { Permission, canModifyTask } from '../utils/permissions';
import { Task } from '../api/taskApi';
import { useAuthenticatedUser } from '../contexts/AuthenticatedUserInfoContext';

interface TaskTableProps {
  tasks: Task[];
  getProjectName: (task: Task) => string;
  getUserNames: (userIds: string[] | undefined) => string;
  getPriorityColor: (priority: string) => string;
  getStatusColor: (status: string) => string;
  handleViewTask: (task: Task) => void;
  handleEditTask: (task: Task) => void;
  handleDeleteTask: (task: Task) => void;
}


const TaskTable: React.FC<TaskTableProps> = ({
  tasks,
  getProjectName,
  getUserNames,
  getPriorityColor,
  getStatusColor,
  handleViewTask,
  handleEditTask,
  handleDeleteTask,
}) => {
  const [search, setSearch] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(10);
  const navigate = useNavigate();

  const { authenticatedUser } = useAuthenticatedUser();

  // Helper for task assignment or ownership check
  const isUserAssignedOrOwner = (task: Task) => {
    if (!authenticatedUser) return false;
    // Task owner check (should always be able to view/edit/delete)
    if (task.createdBy && typeof task.createdBy === 'string' && task.createdBy === authenticatedUser.LoggedInUserId) {
      return true;
    }
    if (task.createdBy && typeof task.createdBy === 'object' && '_id' in task.createdBy && (task.createdBy as { _id: string })._id === authenticatedUser.LoggedInUserId) {
      return true;
    }
    // Assigned user check
    if (!task.assignedToUser) return false;
    return (task.assignedToUser as (string | { _id: string })[]).some((u: string | { _id: string }) => {
      if (typeof u === 'string') return u === authenticatedUser.LoggedInUserId;
      if (u && typeof u === 'object' && '_id' in u) return (u as { _id: string })._id === authenticatedUser.LoggedInUserId;
      return false;
    });
  };

  const canEditTask = (task: Task) =>
    canModifyTask(authenticatedUser, isUserAssignedOrOwner(task));

  // Filter tasks by search
  const filteredTasks = React.useMemo(() => {
    if (!search) return tasks;
    const lower = search.toLowerCase();
    return tasks.filter(task =>
      (task.taskTitle && task.taskTitle.toLowerCase().includes(lower)) ||
      (task.taskId && task.taskId.toLowerCase().includes(lower)) ||
      (task.description && task.description.toLowerCase().includes(lower)) ||
      (task.type && task.type.toLowerCase().includes(lower)) ||
      (getProjectName(task) && getProjectName(task).toLowerCase().includes(lower))
    );
  }, [search, tasks, getProjectName]);

  // Pagination logic
  const totalPages = Math.ceil(filteredTasks.length / perPage) || 1;
  const paginatedTasks = React.useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredTasks.slice(start, start + perPage);
  }, [filteredTasks, currentPage, perPage]);

  React.useEffect(() => {
    // Reset to first page if search or perPage changes
    setCurrentPage(1);
  }, [search, perPage]);

  return (
    <>
      <HStack mb={3} justify="space-between" align="center" px={5} py={2} bg="gray.100" borderRadius="md">
        <HStack spacing={2}>
          <Text fontWeight="bold">Search:</Text>
          <Input
            size="sm"
            placeholder="Search tasks..."
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            width="300px"
            borderRadius={'full'}
            bg={'white'}
          />
        </HStack>
        <HStack spacing={2}>
          <Text fontWeight="bold">Display per page:</Text>
          <Select
            size="sm"
            value={perPage}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPerPage(Number(e.target.value))}
            width="150px"
            borderRadius={'full'}
            bg={'white'}
          >
            {[5, 10, 20, 50, 100].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </Select>
        </HStack>
      </HStack>
      <Table variant="simple">
        <Thead bg="gray.50">
          <Tr>
            <Th>Task ID</Th>
            <Th>Title</Th>
            <Th>Project</Th>
            <Th>Type</Th>
            <Th>Priority</Th>
            <Th>Status</Th>
            <Th>Assigned To</Th>
            <Th>Price</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {paginatedTasks.map((task: Task) => (
            <Tr key={task._id} _hover={{ bg: 'gray.50' }}>
              <Td fontWeight="bold" color="blue.600">
                {task.taskId}
              </Td>
              <Td maxW="250px">
                <Text noOfLines={2} title={task.description}>
                  {task.taskTitle}
                </Text>
              </Td>
              <Td maxW="200px">
                <Text fontSize="sm" color="purple.600" noOfLines={1}>
                  {getProjectName(task)}
                </Text>
              </Td>
              <Td>
                <Badge variant="outline" colorScheme="purple">
                  {task.type}
                </Badge>
              </Td>
              <Td>
                <Badge colorScheme={getPriorityColor(task.priority)}>
                  {task.priority.toUpperCase()}
                </Badge>
              </Td>
              <Td>
                <Badge colorScheme={getStatusColor(task.status)}>{task.status}</Badge>
              </Td>
              <Td>
                <Text fontSize="sm" color="teal.600">
                  {getUserNames(task.assignedToUser)}
                </Text>
              </Td>
              <Td>
                <Text fontSize="sm" color="green.600" fontWeight="bold">
                  {task.quotedPrice ? `$${task.quotedPrice.toLocaleString()}` : '-'}
                </Text>
              </Td>
              <Td>
                <HStack spacing={1}>
                  <Tooltip label="View Details">
                    <IconButton
                      aria-label="View task"
                      icon={<Icon as={FaEye} />}
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => navigate(`/dashboard/task/${task._id}/details`)}
                    />
                  </Tooltip>
                  <Tooltip label="Edit Task">
                    <PermissionGuard permission={Permission.EDIT_TASKS}>
                      <IconButton
                        aria-label="Edit task"
                        icon={<Icon as={FaEdit} />}
                        size="sm"
                        variant="ghost"
                        colorScheme="green"
                        onClick={() => canEditTask(task) && handleEditTask(task)}
                        isDisabled={!canEditTask(task)}
                        tabIndex={!canEditTask(task) ? -1 : 0}
                      />
                    </PermissionGuard>
                  </Tooltip>
                  <PermissionGuard permission={Permission.DELETE_TASKS}>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        aria-label="More options"
                        icon={<Icon as={FaEllipsisV} />}
                        size="sm"
                        variant="ghost"
                        isDisabled={!canEditTask(task)}
                        tabIndex={!canEditTask(task) ? -1 : 0}
                      />
                      <MenuList>
                        <MenuItem
                          icon={<Icon as={FaTrash} />}
                          color="red.500"
                          onClick={() => canEditTask(task) && handleDeleteTask(task)}
                          isDisabled={!canEditTask(task)}
                          tabIndex={!canEditTask(task) ? -1 : 0}
                        >
                          Delete Task
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </PermissionGuard>
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      {/* Pagination Controls */}
      <HStack mt={4} justify="center" spacing={2} pt={2} pb={5} borderRadius="md">
        <IconButton
          aria-label="First page"
          icon={<span>{'<<'}</span>}
          size="sm"
          onClick={() => setCurrentPage(1)}
          isDisabled={currentPage === 1}
        />
        <IconButton
          aria-label="Previous page"
          icon={<span>{'<'}</span>}
          size="sm"
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          isDisabled={currentPage === 1}
        />
        <Text fontSize="sm" fontWeight="bold">
          Page {currentPage} of {totalPages}
        </Text>
        <IconButton
          aria-label="Next page"
          icon={<span>{'>'}</span>}
          size="sm"
          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          isDisabled={currentPage === totalPages}
        />
        <IconButton
          aria-label="Last page"
          icon={<span>{'>>'}</span>}
          size="sm"
          onClick={() => setCurrentPage(totalPages)}
          isDisabled={currentPage === totalPages}
        />
      </HStack>
    </>
  );
};

export default TaskTable;
